import {
  fireReferralLinkedFailed,
  type TReferralLinkedFailedReason,
} from '~/posthogs/events/Referrals';
import { isReferralCodeFormatValid, normalizeReferralCode } from './referralCode';
import { readPendingReferralCode } from './referralCodeStorage';
import { referralService } from './referralService';

/**
 * The only referral error the field shows.
 *
 * One message for two causes — a code that cannot exist (shape) and a code that
 * does not exist (`no_match`) — because the difference is ours, not the user's:
 * either way the code in front of them is wrong and retyping it is the fix. It
 * names no format, so it tells an enumerator nothing a referral link does not.
 */
export const REFERRAL_CODE_INVALID_MESSAGE =
  'Referral code invalid. Check your code or leave blank.';

/**
 * What the gate actually decided, as opposed to what it allowed.
 *
 * `allowed` collapses five of these into `true`, which is right for the form
 * but useless for the funnel: a confirmed referrer, an outage and a breaker
 * trip all read as "sign-up proceeded". The outcome keeps them apart so the
 * caller can confirm a real link (GOTU-8354) without re-deriving the reason.
 *
 * `skipped` is the blank field — no code, no request, nothing to report.
 */
export type TReferralSignUpGateOutcome =
  | 'skipped'
  | 'ok'
  | 'invalid'
  | 'no_match'
  | 'unavailable'
  | 'server_error';

export type TReferralSignUpGateResult =
  | { allowed: true; outcome: Exclude<TReferralSignUpGateOutcome, 'no_match'> }
  | { allowed: false; outcome: 'invalid' | 'no_match'; message: string };

const blocked = (outcome: 'invalid' | 'no_match', code: string): TReferralSignUpGateResult => {
  fireReferralLinkedFailed({ reason: outcome, referral_code: code });

  return { allowed: false, outcome, message: REFERRAL_CODE_INVALID_MESSAGE };
};

/**
 * Allowed, but nothing was confirmed: the lookup declined (`unavailable`) or
 * never answered (`server_error`). Reported as a failure because no referrer
 * was linked, even though sign-up proceeds.
 */
const allowedWithoutConfirmation = (
  outcome: Extract<TReferralLinkedFailedReason, 'unavailable' | 'server_error'>,
  code: string,
): TReferralSignUpGateResult => {
  fireReferralLinkedFailed({ reason: outcome, referral_code: code });

  return { allowed: true, outcome };
};

/**
 * Decides whether sign-up may proceed with the referral code in the field,
 * records a hand-typed arrival on the way past, and reports the outcome to
 * PostHog.
 *
 * Runs only at sign-up submit. Rules, in order:
 *
 * 1. **Blank allows, with no request.** The field is optional.
 * 2. **A code of the wrong shape blocks, with no request of any kind.** Not the
 *    lookup, and not the claim either. Both verbs share one path
 *    (`REFERRAL_CLAIMS_PATH`), so junk on the POST plausibly feeds the same
 *    breaker the shape rule exists to stay clear of — and a three-character
 *    typo is not a referral arrival worth a row. See `referralCode` for why the
 *    client holds the alphabet at all.
 * 3. **A hand-typed code is claimed, fire-and-forget, before the verdict.** The
 *    claim counts arrivals, so it must not depend on whether the code turns out
 *    to resolve — a typed miss counts exactly as an invalid link does.
 * 4. **`no_match` blocks. `ok`, `unavailable` and `server_error` all allow.**
 *
 * The 2026-09-22 decision with referral-service is what puts rules 2-4 here,
 * and it partly reverses the 2026-09-21 ruling this comment used to record.
 * `unavailable` no longer blocks: it means the enumeration breaker tripped, not
 * that the code is bad, and it fires for the innocent office behind a shared IP
 * as readily as for the abuser. Blocking on it would cost a real referral — and
 * a registration — to punish a server-side alarm. The office registers, the
 * code rides along, only the referrer's name is missing. So `no_match` is the
 * single blocking status, which is also why one message suffices where there
 * were two.
 *
 * **The events fire from here, not from the form** (GOTU-8354). This is the one
 * place that knows which of the four failures occurred; deriving that again at
 * the call site would duplicate the mapping and let the two drift.
 *
 * Mirrors mobile-app#5626 and #5632; GOTU-7317 requires the two clients to
 * decide alike, and GOTU-8354 requires them to report alike.
 */
export const gateSignUpOnReferralCode = async (
  rawCode: string | null | undefined,
): Promise<TReferralSignUpGateResult> => {
  const code = normalizeReferralCode(rawCode);
  if (!code) return { allowed: true, outcome: 'skipped' };

  if (!isReferralCodeFormatValid(code)) return blocked('invalid', code);

  /**
   * Hand-typed, or carried by the link that is still in storage?
   *
   * `ReferralLinkRedirect` claims at link arrival, so claiming again here would
   * double-count every referred office. The in-process guard inside
   * `recordReferralClaim` cannot catch that: the link arrival, the redirect and
   * this form can straddle a reload, so the claim often happened on an *earlier
   * page load* and its `Set` is empty again. The stored code survives that gap,
   * which is what makes it the discriminator.
   *
   * Storage empty, or holding a different code, means the code in the field was
   * typed (or edited after a link prefilled it) and has never been claimed.
   *
   * Retyping the stored code by hand reads as a link arrival and is not claimed
   * twice. That is the right call anyway — it is the same code, already counted.
   *
   * NEVER awaited: the service swallows its own failures, and a claim must
   * never delay or fail a registration.
   */
  if (code !== readPendingReferralCode()) {
    void referralService.recordReferralClaim(code);
  }

  const { status } = await referralService.lookupReferrerByCode(code);

  if (status === 'no_match') return blocked('no_match', code);
  if (status === 'unavailable' || status === 'server_error') {
    return allowedWithoutConfirmation(status, code);
  }

  // No success event here, deliberately. `ok` means the code resolved, which is
  // not yet a referral: the sign-up it rides can still fail, and an
  // already-registered email used to emit a success for an account that was
  // never created. `SignUpForm` owns that event now and fires it on the sign-up
  // response.
  //
  // So success and failure are no longer emitted at the same point. That
  // asymmetry is the intended reading: a code that did not resolve did not
  // resolve, whatever becomes of the sign-up.
  return { allowed: true, outcome: 'ok' };
};
